/**
 * @file The root entry point for the application. This file sets up the foundational contexts, configurations,
 * and base styles for the entire application.
 *
 * The `BrowserRouter` is included at this high level to ensure that React's context-based routing is available
 * throughout the entire component tree, allowing for easy use of React Router's hooks and components anywhere in the app.
 *
 * The `Auth0ProviderWithNavigate` is a custom provider that wraps the base `Auth0Provider`. It's integrated with
 * React Router's `useNavigate` hook, ensuring Auth0 authentication context is available to all components and
 * that after authentication, the user is redirected appropriately.
 *
 * The `ThemeProvider` wraps the entire application to provide a consistent theme based on the `EBABTheme`.
 * By applying it at the top level, all child components can access and use the defined theme properties
 * for styling, ensuring a consistent look and feel across the app.
 *
 * The `CssBaseline` is a component from MUI that helps with consistent baseline styling. It effectively
 * resets and normalises many default browser styles to ensure that the app's components render consistently
 * across different browsers. By including it at the top level, these baseline styles are applied throughout the app.
 *
 * `React.StrictMode` is a tool for highlighting potential problems in an application. It intentionally
 * does additional checks and warnings for its descendants. By wrapping the entire application with StrictMode,
 * developers are made aware of potential issues and discouraged from using deprecated features. This aids
 * in catching problems early and making future migrations smoother.
 *
 * The `injectHeapScript` function is called to dynamically inject the Heap Analytics script into the HTML document.
 * It determines the appropriate Heap ID based on the environment and ensures the script is only injected once.
 * This setup enables Heap Analytics tracking throughout the application.
 */
import CssBaseline from '@mui/material/CssBaseline';
import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './index.css';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { Auth0ProviderWithNavigate } from './auth0-provider-with-navigate';
import { ThemeContextWrapper } from './context';
import { injectHeapScript } from './functions/heapAnalytics';

FreshworksWidget('hide', 'launcher');
injectHeapScript();

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
    <React.StrictMode>
        <BrowserRouter>
            <ThemeContextWrapper>
                <CssBaseline />
                <Auth0ProviderWithNavigate>
                    <App />
                </Auth0ProviderWithNavigate>
            </ThemeContextWrapper>
        </BrowserRouter>
    </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
